import { v4 as uuidv4 } from "uuid";
import { useToast } from "@chakra-ui/react";

import {
  Dictionary,
  getCenteredBBox,
  getRandomHash,
  Lang,
  Logger,
  safeCall,
  shallowCopy,
  sleep,
} from "@noli/core";
import { BoardStore, translate } from "@noli/business";

import { tasksStore, TaskType } from "@/stores/tasks";
import { wenxinStore } from "@/thirdParty/wenxin/store";
import { addNewImage, toast } from "@/utils/misc";
import {
  addVirtualNode,
  postDrawRecord,
  recoverVirtualNode,
  VirtualNodeInfo,
} from "@/utils/safeOp";
import { loginStore } from "@/modules/login/store";
import { Editor_Words } from "@/utils/lang/editor";
import { getRandomInt } from "@/utils/random";
import { WenxinMeta } from "./meta";
import { Wenxin_Words } from "./lang";
import Emitter, { MessageType } from "@/utils/events";

const WENXIN_NUM = 3;

function getWenxinBody({ data: { text, size, style, url = "" } }: WenxinMeta): Dictionary<string> {
  return { text, style, resolution: size, url };
}

export async function postWenxin({
  t,
  lang,
  meta,
}: {
  t: ReturnType<typeof useToast>;
  lang: Lang;
  meta: WenxinMeta;
}): Promise<void> {
  const createTime = Date.now() / 1000;
  const virtualInfo = meta.data.wh;
  const virtualNodeInfoList: VirtualNodeInfo[] = [];
  for (let i = 0; i < WENXIN_NUM; i++) {
    if (i === 0) {
      toast(t, "success", translate(Editor_Words["submit-success-message"], lang));
    }
    virtualNodeInfoList.push({
      alias: await addVirtualNode(t, ".wenxin.", lang, virtualInfo),
    });
    await sleep(500);
  }
  const taskType = meta.data.url ? "wenxin-img2img" : "wenxin";
  const task: TaskType = {
    key: uuidv4(),
    type: taskType,
    status: "working",
    pending: 0,
    isKafka: false,
    taskPack: { uri: "", data: {} },
  };
  tasksStore.addTask(task);
  const failed = async (needToast = true) => {
    if (needToast) {
      toast(t, "error", translate(Wenxin_Words["wenxin-error-message"], lang));
    }
    tasksStore.removeTask(task);
    for (let i = 0; i < WENXIN_NUM; i++) {
      await recoverVirtualNode(virtualNodeInfoList[i]);
    }
  };
  // create task
  const createRes = await safeCall(
    () =>
      fetch(`${process.env.REACT_APP_MAIN_URL}/ai_creator/wenxin/push`, {
        method: "POST",
        headers: loginStore.jsonPostForceAuthReqHeaders,
        body: JSON.stringify(getWenxinBody(meta)),
      }),
    {
      success: async () => void 0,
      failed: async () => void 0,
    },
  );

  if (!createRes || !createRes.ok) return failed();
  const createData = await createRes.json();
  if (Logger.isDebug) {
    console.log(">> createData", createData);
  }
  if (createData.code !== 0) {
    let needToast = true;
    if (createData.msg === "ERR_BALANCE_NOT_ENOUGH") {
      toast(t, "error", translate(Wenxin_Words["wenxin-balance-not-enough-message"], lang));
      Emitter.emit(MessageType.HEART_COIN, { type: MessageType.HEART_COIN, data: { value: true } });
      needToast = false;
    }
    if (createData.msg?.includes("code=1114")) {
      toast(t, "warning", translate(Editor_Words["post-creator-not-safe-warning-message"], lang));
      needToast = false;
    }
    return failed(needToast);
  }
  const uid = createData.data?.uid;
  if (!uid) return failed();
  // query task
  let imgURLs: string[] = [];
  let nextSleep = 10000;
  let failedCounter = 0;
  const failedPatience = 3;
  while (imgURLs.length === 0 && tasksStore.tasks.get(task.key)) {
    await sleep(nextSleep);
    const queryRes = await safeCall(
      () =>
        fetch(`${process.env.REACT_APP_MAIN_URL}/ai_creator/wenxin/status`, {
          method: "POST",
          headers: loginStore.jsonPostForceAuthReqHeaders,
          body: JSON.stringify({ uid }),
        }),
      {
        success: async () => void 0,
        failed: async () => void 0,
      },
    );
    if (!queryRes || !queryRes.ok) {
      if (Logger.isDebug) {
        console.log(">> query wenxin failed:", queryRes, await queryRes?.text());
      }
      nextSleep = 1000;
      failedCounter += 1;
      if (failedCounter >= failedPatience) return failed();
      continue;
    }
    const queryData = await queryRes.json();
    const waiting = queryData.data?.waiting;
    if (Logger.isDebug) {
      console.log(">> queryData", queryData);
      console.log(`>> model is painting now!, taskId: ${uid}, waiting: ${waiting}`);
    }
    if (queryData.code !== 0) {
      return failed();
    }
    imgURLs = queryData.data?.img_urls ?? [];
    nextSleep =
      waiting !== "30s" ? 10000 : nextSleep === 10000 ? 1000 : 1000 + getRandomInt(0, 10) * 1000;
  }
  if (!tasksStore.tasks.get(task.key)) {
    for (let i = 0; i < WENXIN_NUM; i++) {
      await recoverVirtualNode(virtualNodeInfoList[i]);
    }
    wenxinStore.fetchWenxinStatus();
    return;
  }
  for (let i = 0; i < WENXIN_NUM; i++) {
    let bbox = await recoverVirtualNode(virtualNodeInfoList[i]);
    if (!bbox) {
      bbox = getCenteredBBox(virtualInfo.w, virtualInfo.h, BoardStore.board);
    }
    const url = imgURLs[i];
    if (!url) continue;
    const newAlias = `wenxin.${getRandomHash()}`;
    const iMeta = shallowCopy(meta);
    iMeta.data.timestamp = Date.now();
    addNewImage(newAlias, url, {
      info: bbox,
      meta: iMeta,
      callbacks: {
        success: async () => {
          postDrawRecord(url, Date.now() / 1000 - createTime, iMeta);
          if (i === WENXIN_NUM - 1) {
            toast(t, "success", translate(Editor_Words["generate-success-message"], lang));
          }
        },
        failed: async () => console.error(`Failed to add Wenxin at ${i}`),
      },
    });
  }
  tasksStore.removeTask(task);
  wenxinStore.fetchWenxinStatus();
}
